import React, { useEffect, useMemo, useState } from "react";
import PageShell from "@/components/PageShell";
import { api } from "@/lib/api";
import { ExternalLink, PlayCircle, Newspaper } from "lucide-react";
import { SampleTag, fmtDate } from "@/components/AuctionCard";

export default function MediaCoverage() {
    const [items, setItems] = useState(null);
    const [cats, setCats] = useState([]);
    const [cat, setCat] = useState("All");
    useEffect(() => {
        api.get("/media").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
        api.get("/media/meta").then((r) => setCats(r.data.categories)).catch(() => {});
    }, []);
    const shown = useMemo(() => (items || []).filter((m) => cat === "All" || m.category === cat), [items, cat]);

    return (
        <PageShell navVariant="dark">
            <section className="bg-[#0a0d18] text-white pt-32 pb-14" data-testid="media-hero">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--ebg-gold)]"><Newspaper size={13}/> About · Media Coverage</div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4">EBG Realty <em className="not-italic text-[var(--ebg-gold)]">in the news.</em></h1>
                    <p className="text-white/70 mt-5 max-w-xl text-sm md:text-base">Press, interviews, events and announcements. Each story links to its original source.</p>
                </div>
            </section>
            <section className="max-w-[1440px] mx-auto px-6 md:px-10 py-12" data-testid="media-list">
                <div className="flex flex-wrap gap-2 mb-10">
                    {["All", ...cats].map((c) => (
                        <button key={c} onClick={() => setCat(c)} data-testid={`media-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className={`px-4 py-2 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors ${cat === c ? "bg-black text-white" : "border border-black/15 text-black/60 hover:text-black hover:border-black"}`}>{c}</button>
                    ))}
                </div>
                {items === null ? <div className="text-black/40 py-20 text-center">Loading…</div> : shown.length === 0 ? (
                    <div className="border border-dashed border-black/15 py-20 text-center text-black/55" data-testid="media-empty">Media coverage will be published here soon.</div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {shown.map((m) => (
                            <article key={m.id} className="bg-white border border-black/8 flex flex-col" data-testid={`media-item-${m.id}`}>
                                <div className="relative aspect-[16/10] bg-black/5 overflow-hidden">
                                    {m.image && <img src={m.image} alt={m.title} loading="lazy" className="w-full h-full object-cover"/>}
                                    <div className="absolute top-3 left-3 flex gap-2">
                                        <span className="bg-[var(--ebg-gold)] text-black px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-bold">{m.category}</span>
                                        <SampleTag show={m.is_sample}/>
                                    </div>
                                </div>
                                <div className="p-5 flex flex-col flex-1">
                                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-black/50">{m.publication} · {fmtDate(m.date)}</div>
                                    <h2 className="font-serif text-xl leading-snug mt-2">{m.title}</h2>
                                    {m.description && <p className="text-black/60 text-[13.5px] mt-2 leading-relaxed flex-1">{m.description}</p>}
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {m.article_url && <a href={m.article_url} target="_blank" rel="noopener noreferrer" data-testid={`media-article-${m.id}`} className="inline-flex items-center gap-1.5 border border-black/15 px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-bold hover:border-black">Read Article <ExternalLink size={11}/></a>}
                                        {m.video_url && <a href={m.video_url} target="_blank" rel="noopener noreferrer" data-testid={`media-video-${m.id}`} className="inline-flex items-center gap-1.5 bg-black text-white px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black">Watch Video <PlayCircle size={12}/></a>}
                                        {!m.article_url && !m.video_url && <span className="text-[11px] text-black/40">Original link coming soon</span>}
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </section>
        </PageShell>
    );
}
